import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { appUrl, env } from '@astro/config'
import { useAuth } from '@astro/auth'
import { apiErrorMessage, payForPujaBooking } from '@astro/payments'
import { usePujaBookings } from '../useDashboard'

/* ─────────────────────────────────────────────────────────────
   Puja Bookings tab — the seeker's Pooja Energization history
   (GET /my-bookings). Every booking action lives here: details,
   invoice download, cancel, complete-payment for a reserved
   booking, and "Book Again" (reorder-puja → straight into the
   Razorpay popup via the shared @astro/payments driver).
   ───────────────────────────────────────────────────────────── */

/* Inline lucide-style icons — same chrome as OrdersTab. */
const Icon = ({ children, className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
)
const FlameIcon = (props) => (
  <Icon {...props}><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" /></Icon>
)
const CalendarIcon = (props) => (
  <Icon {...props}><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>
)
const FileIcon = (props) => (
  <Icon {...props}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M12 18v-6" /><path d="m9 15 3 3 3-3" /></Icon>
)
const RepeatIcon = (props) => (
  <Icon {...props}><path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" /></Icon>
)
const CardIcon = (props) => (
  <Icon {...props}><rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" /></Icon>
)
const EyeIcon = (props) => (
  <Icon {...props}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Icon>
)
const MapPinIcon = (props) => (
  <Icon {...props}><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="12" r="3" /></Icon>
)
const UsersIcon = (props) => (
  <Icon {...props}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>
)

const pill = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ring-1 ring-inset ring-black/5'

/* booking_status / payment_status → pill colourways. */
const BOOKING_CLS = {
  pending: 'bg-amber-50 text-amber-700',
  confirmed: 'bg-emerald-50 text-emerald-700',
  completed: 'bg-sky-50 text-sky-700',
  cancelled: 'bg-red-50 text-red-600',
}
const PAYMENT_CLS = {
  pending: 'bg-amber-50 text-amber-700',
  paid: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-600',
}

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
]

const rupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`

/** "2026-08-04" (date-only) or a full ISO stamp → "4 Aug 2026". */
const formatDate = (value) => {
  if (!value) return ''
  const d = new Date(String(value).length === 10 ? `${value}T00:00:00` : value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** /download-invoice streams a PDF over the session cookie — a plain link
 * against the same API base the axios client uses works as-is. */
const invoiceHref = (id) => `${String(env.VITE_API_BASE_URL).replace(/\/$/, '')}${api.puja.downloadInvoicePath(id)}`

const actionBtn = 'inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none'

/* ── Skeleton while the first page loads ── */
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-stone-100 bg-white overflow-hidden animate-pulse">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 sm:py-4 border-b border-stone-100">
        <div className="w-10 h-10 rounded-xl bg-stone-100 shrink-0" />
        <div className="space-y-2">
          <div className="h-4 w-44 rounded bg-stone-100" />
          <div className="h-3 w-28 rounded bg-stone-100" />
        </div>
        <div className="ml-auto flex gap-2">
          <div className="h-7 w-24 rounded-full bg-stone-100" />
          <div className="h-7 w-20 rounded-full bg-stone-100" />
        </div>
      </div>
      <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5 sm:py-4">
        <div className="w-16 h-16 rounded-xl bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-4 w-2/3 rounded bg-stone-100" />
          <div className="h-3 w-1/3 rounded bg-stone-100" />
        </div>
        <div className="h-6 w-20 rounded bg-stone-100 shrink-0" />
      </div>
      <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-t border-stone-100 bg-stone-50/50">
        <div className="h-7 w-64 rounded-full bg-stone-100" />
        <div className="h-6 w-24 rounded bg-stone-100" />
      </div>
    </div>
  )
}

/* ── One booking row ── */
function BookingCard({ booking: b, busy, onDetails, onCancel, onPay, onReorder }) {
  const paid = b.payment_status === 'paid'
  // A reserved-but-unpaid booking is finished with Complete Payment;
  // anything past that stage can only be re-booked as a fresh clone.
  const canPay = !paid && b.booking_status === 'pending'
  const canCancel = b.booking_status === 'pending' || b.booking_status === 'confirmed'
  const canReorder = b.booking_status !== 'pending'
  const isBusy = busy?.id === b.id

  return (
    <div className="rounded-2xl border border-amber-900/10 bg-white overflow-hidden shadow-sm hover:shadow-lg hover:shadow-amber-900/5 hover:border-amber-200/80 hover:-translate-y-0.5 transition-all duration-300">
      {/* header — booking identity left, status pills right */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 sm:px-5 py-3.5 sm:py-4 bg-gradient-to-r from-amber-50/70 via-white to-white border-b border-stone-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-amber-100/80 text-amber-700 flex items-center justify-center shrink-0">
            <FlameIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="num text-base font-bold text-astroDark truncate">{b.booking_number}</p>
            <div className="flex items-center gap-3 mt-0.5 text-sm text-stone-400">
              {b.created_at && (
                <span className="inline-flex items-center gap-1.5">Booked {formatDate(b.created_at)}</span>
              )}
              {b.puja_date && (
                <span className="inline-flex items-center gap-1.5 text-amber-700 font-semibold">
                  <CalendarIcon className="w-3.5 h-3.5" /> Puja on {formatDate(b.puja_date)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          <span className={`${pill} ${BOOKING_CLS[b.booking_status] ?? 'bg-stone-100 text-stone-500'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
            {b.booking_status}
          </span>
          <span className={`${pill} px-2.5 py-0.5 ${PAYMENT_CLS[b.payment_status] ?? 'bg-stone-100 text-stone-500'}`}>
            <CardIcon className="w-3.5 h-3.5" /> {b.payment_status}
          </span>
        </div>
      </div>

      {/* the puja + package this booking is for */}
      <div className="flex items-center gap-4 px-4 sm:px-5 py-3.5 sm:py-4">
        {b.puja?.image ? (
          <img src={b.puja.image} alt={b.puja?.title} className="w-16 h-16 rounded-xl object-cover bg-amber-50 ring-1 ring-stone-100 shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-amber-50 ring-1 ring-amber-100/60 flex items-center justify-center text-amber-300 shrink-0">
            <FlameIcon className="w-7 h-7" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {b.puja?.slug ? (
            <a
              href={`${appUrl('shop')}/pooja-energization/${b.puja.slug}`}
              className="text-base font-semibold text-astroDark truncate block hover:text-amber-700 transition-colors"
            >
              {b.puja?.title || 'Pooja Booking'}
            </a>
          ) : (
            <p className="text-base font-semibold text-astroDark truncate">{b.puja?.title || 'Pooja Booking'}</p>
          )}
          {b.package && (
            <p className="text-sm text-stone-400 mt-1 inline-flex items-center gap-1.5">
              <UsersIcon className="w-3.5 h-3.5" />
              {b.package.title} package · up to {b.package.max_devotees} devotee{b.package.max_devotees !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="sm:text-right shrink-0">
          {b.discount_amount > 0 && (
            <p className="text-xs text-stone-400">
              <span className="num line-through">{rupees(b.original_amount)}</span>
              <span className="text-green-600 font-semibold ml-1.5">saved {rupees(b.discount_amount)}</span>
            </p>
          )}
          <p className="num text-2xl font-bold text-astroDark leading-tight">{rupees(b.final_amount)}</p>
        </div>
      </div>

      {/* footer — every action this booking's state allows */}
      <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 py-3.5 sm:py-4 border-t border-stone-100 bg-stone-50/60">
        {canPay && (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onPay(b)}
            className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full shadow-sm hover:shadow-md transition-all disabled:opacity-40 disabled:pointer-events-none"
          >
            {isBusy && busy?.action === 'pay' ? 'Opening payment…' : 'Complete Payment'}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDetails(b)}
          className={`${actionBtn} border-stone-200 bg-white text-astroDark hover:bg-amber-50`}
        >
          <EyeIcon className="w-3.5 h-3.5" /> Details
        </button>
        {paid && (
          <a
            href={invoiceHref(b.id)}
            target="_blank"
            rel="noopener noreferrer"
            className={`${actionBtn} border-stone-200 bg-white text-astroDark hover:bg-amber-50`}
          >
            <FileIcon className="w-3.5 h-3.5" /> Invoice
          </a>
        )}
        {canReorder && (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onReorder(b)}
            className={`${actionBtn} border-amber-300/70 bg-white text-amber-700 hover:bg-amber-500 hover:border-amber-500 hover:text-white`}
          >
            <RepeatIcon className="w-3.5 h-3.5" />
            {isBusy && busy?.action === 'reorder' ? 'Recreating…' : 'Book Again'}
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onCancel(b)}
            className={`${actionBtn} ml-auto border-red-200 bg-white text-red-500 hover:bg-red-50`}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}

/* ── Small labelled fact for the details modal ── */
function Fact({ label, children }) {
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-stone-400">{label}</p>
      <p className="text-sm font-semibold text-astroDark mt-0.5">{children || '—'}</p>
    </div>
  )
}

/* ── Details modal — the full record (devotees + address) per booking ── */
function BookingDetailsModal({ bookingId, onClose }) {
  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const query = useQuery({
    queryKey: ['dashboard', 'puja-booking', bookingId],
    queryFn: () => api.puja.bookingDetails(bookingId),
    retry: false,
    staleTime: 60_000,
    select: (res) => res?.data ?? null,
  })
  const b = query.data

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Booking details"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Pooja Energization</span>
            <h3 className="text-xl text-astroDark font-royal">{b?.booking_number || 'Booking Details'}</h3>
            {b?.invoice_number && <p className="num text-xs text-stone-400 mt-0.5">Invoice · {b.invoice_number}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {query.isPending ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-16 rounded-xl bg-stone-100" />
            <div className="h-24 rounded-xl bg-stone-100" />
            <div className="h-24 rounded-xl bg-stone-100" />
          </div>
        ) : query.isError || !b ? (
          <div className="py-8 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn’t load this booking</p>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-3 text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* puja + statuses */}
            <div className="flex items-center gap-4 p-4 rounded-xl border border-stone-100 bg-amber-50/40">
              {b.puja?.image ? (
                <img src={b.puja.image} alt={b.puja?.title} className="w-14 h-14 rounded-xl object-cover bg-amber-50 ring-1 ring-stone-100 shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-amber-100/80 flex items-center justify-center text-amber-500 shrink-0"><FlameIcon className="w-6 h-6" /></div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-astroDark truncate">{b.puja?.title || 'Pooja Booking'}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className={`${pill} px-2.5 py-0.5 ${BOOKING_CLS[b.booking_status] ?? 'bg-stone-100 text-stone-500'}`}>{b.booking_status}</span>
                  <span className={`${pill} px-2.5 py-0.5 ${PAYMENT_CLS[b.payment_status] ?? 'bg-stone-100 text-stone-500'}`}>{b.payment_status}</span>
                  {b.delivery_status && (
                    <span className={`${pill} px-2.5 py-0.5 bg-stone-100 text-stone-500`}>delivery · {b.delivery_status}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Fact label="Puja Date">{formatDate(b.puja_date)}</Fact>
              <Fact label="Booked On">{formatDate(b.created_at)}</Fact>
              <Fact label="Package">{b.package?.title}</Fact>
              <Fact label="Amount Paid"><span className="num">{rupees(b.final_amount)}</span></Fact>
            </div>

            {/* devotees the sankalp was taken for */}
            {Array.isArray(b.devotees) && b.devotees.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-2 inline-flex items-center gap-1.5">
                  <UsersIcon className="w-3.5 h-3.5" /> Devotees
                </p>
                <div className="rounded-xl border border-stone-100 divide-y divide-stone-100 overflow-hidden">
                  {b.devotees.map((d) => (
                    <div key={d.id ?? d.name} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 bg-white">
                      <p className="text-sm font-semibold text-astroDark">{d.name}</p>
                      {d.is_primary && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">Primary</span>}
                      <p className="text-xs text-stone-400 ml-auto">
                        {[d.gotra && `Gotra ${d.gotra}`, d.gender, formatDate(d.dob)].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* prasad delivery address */}
            {b.address && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-2 inline-flex items-center gap-1.5">
                  <MapPinIcon className="w-3.5 h-3.5" /> Delivery Address
                </p>
                <div className="rounded-xl border border-stone-100 px-4 py-3 text-sm text-stone-500">
                  <p className="font-semibold text-astroDark">{b.address.name} · <span className="num">{b.address.mobile}</span></p>
                  <p className="mt-0.5">
                    {[b.address.address, b.address.landmark, b.address.city, b.address.state, b.address.pincode].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* money summary + invoice */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-dashed border-stone-200">
              <div className="text-xs text-stone-400 space-y-1">
                <p>Package price <span className="num text-stone-500 ml-1">{rupees(b.original_amount)}</span></p>
                {b.discount_amount > 0 && <p className="text-green-600 font-semibold">Discount <span className="num ml-1">− {rupees(b.discount_amount)}</span></p>}
                <p className="text-astroDark font-bold text-sm">Total <span className="num ml-1">{rupees(b.final_amount)}</span></p>
              </div>
              {b.payment_status === 'paid' && (
                <a
                  href={invoiceHref(b.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${actionBtn} border-amber-300/70 bg-white text-amber-700 hover:bg-amber-500 hover:border-amber-500 hover:text-white`}
                >
                  <FileIcon className="w-3.5 h-3.5" /> Download Invoice
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

/* ── Cancel confirmation modal (optional reason) ── */
function CancelBookingModal({ booking, onClose, onCancelled }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [banner, setBanner] = useState(null)

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const mutation = useMutation({
    mutationFn: () =>
      api.puja.cancelBooking({
        puja_booking_id: booking.id,
        ...(reason.trim() && { reason: reason.trim() }),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'puja-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'puja-booking', booking.id] })
      onCancelled(res?.data ?? booking)
    },
    // Already-cancelled/completed comes back as a business-rule 422 with a
    // readable message — show it as-is.
    onError: (err) => setBanner(apiErrorMessage(err, 'Could not cancel the booking — try again.')),
  })

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cancel booking"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
      >
        <h3 className="text-xl text-astroDark font-royal">Cancel this booking?</h3>
        <p className="text-sm text-stone-500 mt-1.5">
          <span className="num font-semibold text-astroDark">{booking.booking_number}</span> — {booking.puja?.title || 'Pooja booking'}
          {booking.puja_date ? ` on ${formatDate(booking.puja_date)}` : ''}.
        </p>
        {booking.payment_status === 'paid' && (
          <p className="mt-3 px-4 py-3 rounded-xl text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
            Refunds are not automatic — our support team will reach out about your paid amount.
          </p>
        )}

        {banner && (
          <div className="mt-3 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">{banner}</div>
        )}

        <label className="block mt-4">
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Booked the wrong date"
            className="mt-1 w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors resize-none"
          />
        </label>

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-xl border border-stone-200 text-astroDark hover:bg-stone-50 transition-colors"
          >
            Keep Booking
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => { setBanner(null); mutation.mutate() }}
            className="text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {mutation.isPending ? 'Cancelling…' : 'Cancel Booking'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

/* ── Full-screen wait while verify-payment runs after the popup ── */
function VerifyingOverlay() {
  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-[#3D0A0A]/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl px-8 py-6 text-center shadow-2xl">
        <div className="w-10 h-10 mx-auto mb-3 border-4 border-goldPrimary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-bold text-astroDark">Verifying your payment…</p>
        <p className="text-xs text-stone-400 mt-1">Please don’t close or refresh this window.</p>
      </div>
    </div>,
    document.body,
  )
}

/* ─────────────────────────── Tab ─────────────────────────── */
export default function PujaBookingsTab() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const [detailsId, setDetailsId] = useState(null)
  const [cancelTarget, setCancelTarget] = useState(null)
  const [banner, setBanner] = useState(null) // { kind: 'success' | 'error', text }
  const [busy, setBusy] = useState(null) // { id, action: 'pay' | 'reorder' }
  const [verifying, setVerifying] = useState(false)

  const { bookings, hasBookings, isLoading, isFetching, isError, refetch, lastPage, total } = usePujaBookings(page)

  const visible = useMemo(
    () => (statusFilter === 'all' ? bookings : bookings.filter((b) => b.booking_status === statusFilter)),
    [bookings, statusFilter],
  )

  const refreshBookings = () => queryClient.invalidateQueries({ queryKey: ['dashboard', 'puja-bookings'] })

  /** create-order → Razorpay popup → verify, via the shared driver. */
  async function payBooking(booking) {
    setBusy({ id: booking.id, action: 'pay' })
    try {
      const orderRes = await api.puja.createOrder({ puja_booking_id: booking.id })
      if (!orderRes?.success || !orderRes?.data) {
        setBanner({ kind: 'error', text: orderRes?.message || 'Could not start the payment — try again.' })
        return
      }
      // List rows omit devotees — prefill from the reorder clone when we have
      // it, else from the signed-in profile.
      const primary =
        booking.devotees?.find((d) => d.is_primary) ??
        booking.devotees?.[0] ??
        { name: user?.name || '', email: user?.email || '', mobile: user?.mobile || user?.phone || '' }
      await payForPujaBooking({
        booking,
        order: orderRes.data,
        primary,
        onVerifyStart: () => setVerifying(true),
        onSuccess: (confirmed) => {
          setBanner({ kind: 'success', text: `Payment received — booking ${confirmed.booking_number} is confirmed. 🙏` })
        },
        onFailure: (message) => setBanner({ kind: 'error', text: message }),
      })
    } catch (err) {
      setBanner({ kind: 'error', text: apiErrorMessage(err, 'Could not start the payment — try again.') })
    } finally {
      setBusy(null)
      setVerifying(false)
      refreshBookings()
    }
  }

  /** reorder-puja clones the booking, then goes straight into payment. */
  async function reorderBooking(booking) {
    setBanner(null)
    setBusy({ id: booking.id, action: 'reorder' })
    try {
      const res = await api.puja.reorderPuja({ puja_booking_id: booking.id })
      if (!res?.success || !res?.data) {
        setBanner({ kind: 'error', text: res?.message || 'Could not recreate the booking — try again.' })
        return
      }
      refreshBookings()
      setBanner({ kind: 'success', text: `Booking ${res.data.booking_number} recreated — complete the payment to confirm it.` })
      await payBooking(res.data)
    } catch (err) {
      setBanner({ kind: 'error', text: apiErrorMessage(err, 'Could not recreate the booking — try again.') })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="p-4 sm:p-6">
      {verifying && <VerifyingOverlay />}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h3 className="text-xl text-astroDark font-royal">Puja Bookings</h3>
        <div className="flex items-center gap-3">
          {hasBookings && total > 0 && (
            <span className="text-xs uppercase tracking-wider text-stone-400">{total} booking{total !== 1 ? 's' : ''}</span>
          )}
          {/* The storefront lives in the shop app — absolute cross-app URL so
              this shared tab works from every app that mounts the dashboard. */}
          <a
            href={`${appUrl('shop')}/pooja-energization`}
            className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl shadow-sm hover:shadow-lg hover:shadow-goldPrimary/25 transition-all hover:-translate-y-0.5"
          >
            Book a Pooja
          </a>
        </div>
      </div>

      {banner && (
        <div
          className={`flex items-start justify-between gap-3 mb-5 px-4 py-3 rounded-xl text-xs font-semibold border ${
            banner.kind === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'
          }`}
        >
          <span>{banner.text}</span>
          <button type="button" onClick={() => setBanner(null)} aria-label="Dismiss" className="font-bold shrink-0">✕</button>
        </div>
      )}

      {/* status filter chips (filters the loaded page) */}
      {hasBookings && (
        <div className="flex flex-wrap gap-2 mb-5">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatusFilter(t.key)}
              className={`text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full border transition-colors ${
                statusFilter === t.key ? 'bg-[#3D0A0A] text-white border-[#3D0A0A]' : 'border-stone-200 text-stone-500 hover:bg-amber-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : isError ? (
        <div className="py-12 text-center">
          <div className="text-4xl mb-3 opacity-40">🛰️</div>
          <p className="text-base font-semibold text-astroDark mb-1">Couldn’t load your puja bookings</p>
          <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching your booking history.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : !hasBookings ? (
        <div className="py-12 text-center">
          <div className="text-4xl mb-3 opacity-40">🪔</div>
          <p className="text-base font-semibold text-astroDark mb-1">No puja bookings yet</p>
          <p className="text-sm text-stone-400 mb-4">Book a Pooja Energization and it will appear here.</p>
          <a
            href={`${appUrl('shop')}/pooja-energization`}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/70 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-amber-700 shadow-sm hover:bg-amber-500 hover:border-amber-500 hover:text-white transition-all duration-300"
          >
            Explore Poojas
          </a>
        </div>
      ) : visible.length === 0 ? (
        <div className="py-12 text-center">
          <div className="text-4xl mb-3 opacity-40">🪔</div>
          <p className="text-base font-semibold text-astroDark">No {statusFilter} bookings on this page</p>
        </div>
      ) : (
        <>
          <div className={`space-y-6 transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
            {visible.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                busy={busy}
                onDetails={(bk) => setDetailsId(bk.id)}
                onCancel={(bk) => setCancelTarget(bk)}
                onPay={payBooking}
                onReorder={reorderBooking}
              />
            ))}
          </div>

          {lastPage > 1 && (
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-100">
              <p className="text-xs uppercase tracking-wider text-stone-400">
                Page {page} of {lastPage}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => p - 1)}
                  className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  disabled={page >= lastPage || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                  className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg border border-stone-200 text-astroDark hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {detailsId != null && <BookingDetailsModal bookingId={detailsId} onClose={() => setDetailsId(null)} />}
      {cancelTarget && (
        <CancelBookingModal
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={(b) => {
            setCancelTarget(null)
            setBanner({ kind: 'success', text: `Booking ${b.booking_number} has been cancelled.` })
          }}
        />
      )}
    </div>
  )
}
